
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { calculateTypabilityScore, getScoreTier, ScoreBreakdown } from '@/utils/scoreCalculator';
import ScoreGauge from '@/components/ScoreGauge';
import { Link } from 'react-router-dom';

interface ComparisonResult {
  domain: string;
  score: number;
  breakdown: ScoreBreakdown;
  tier: {
    tier: string;
    emoji: string;
  };
}

const CompareDomainsPage = () => {
  const [domains, setDomains] = useState(['', '']);
  const [results, setResults] = useState<ComparisonResult[]>([]);

  const addDomain = () => {
    setDomains([...domains, '']);
  };

  const removeDomain = (index: number) => {
    const newDomains = [...domains];
    newDomains.splice(index, 1);
    setDomains(newDomains);
    
    // Also remove from results if exists
    if (results.length > index) {
      const newResults = [...results];
      newResults.splice(index, 1);
      setResults(newResults);
    }
  };

  const updateDomain = (index: number, value: string) => {
    const newDomains = [...domains];
    newDomains[index] = value;
    setDomains(newDomains);
  };

  const compareAllDomains = () => {
    // Filter out empty domains
    const validDomains = domains.filter(domain => domain.trim() !== '');
    
    if (validDomains.length < 2) {
      return; // Need at least 2 domains to compare
    }
    
    const newResults: ComparisonResult[] = validDomains.map(domain => {
      const breakdown = calculateTypabilityScore(domain);
      const tier = getScoreTier(breakdown.total);
      
      return {
        domain: domain.toLowerCase(),
        score: breakdown.total,
        breakdown,
        tier
      };
    });
    
    // Sort by score (high to low)
    newResults.sort((a, b) => b.score - a.score);
    setResults(newResults);
  };

  return (
    <div className="min-h-screen py-8 bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4">
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-blue-900 mb-2">Domain Comparison</h1>
            <p className="text-xl text-blue-700">
              Compare typability scores of multiple domains
            </p>
          </div>
          <Link to="/">
            <Button variant="outline">Back to Analyzer</Button>
          </Link>
        </header>

        <Card className="shadow-lg mb-8">
          <CardHeader>
            <CardTitle>Enter Domains to Compare</CardTitle>
            <CardDescription>
              Add two or more domains to see how they compare in typability
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {domains.map((domain, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Input
                    placeholder={`Domain ${index + 1} (e.g., example.com)`}
                    value={domain}
                    onChange={(e) => updateDomain(index, e.target.value)}
                    className="flex-grow"
                  />
                  {domains.length > 2 && (
                    <Button 
                      variant="ghost" 
                      onClick={() => removeDomain(index)}
                      className="text-red-500"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              
              <div className="flex justify-between">
                <Button onClick={addDomain} variant="outline">
                  Add Another Domain
                </Button>
                <Button onClick={compareAllDomains}>Compare Domains</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {results.length > 0 && (
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Comparison Results</CardTitle>
              <CardDescription>
                See how your domains compare in terms of typability
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap justify-center gap-6 mb-8">
                {results.map((result, index) => (
                  <div 
                    key={index} 
                    className={`text-center p-6 rounded-lg shadow-sm ${index === 0 ? 'bg-blue-50 ring-2 ring-blue-500' : 'bg-white'}`}
                  >
                    {index === 0 && (
                      <div className="text-sm font-semibold text-blue-500 mb-1">
                        BEST SCORE
                      </div>
                    )}
                    <ScoreGauge score={result.score} size="md" />
                    <div className="mt-3">
                      <h3 className="text-lg font-bold">{result.domain}</h3>
                      <div className="flex items-center justify-center mt-1">
                        <span className="mr-2 text-xl">{result.tier.emoji}</span>
                        <span className="font-medium">{result.tier.tier}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    {results.map((result, index) => (
                      <TableHead key={index} className="text-center">
                        {result.domain}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Total Score</TableCell>
                    {results.map((result, index) => (
                      <TableCell key={index} className="text-center font-bold">
                        {result.score}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Finger Effort</TableCell>
                    {results.map((result, index) => (
                      <TableCell key={index} className="text-center">
                        {result.breakdown.fingerEffort}/20
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Hand Alternation</TableCell>
                    {results.map((result, index) => (
                      <TableCell key={index} className="text-center">
                        {result.breakdown.handAlternation}/20
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Repetition Complexity</TableCell>
                    {results.map((result, index) => (
                      <TableCell key={index} className="text-center">
                        {result.breakdown.repetitionComplexity}/20
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Length & Simplicity</TableCell>
                    {results.map((result, index) => (
                      <TableCell key={index} className="text-center">
                        {result.breakdown.lengthSimplicity}/20
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Phonetic Clarity</TableCell>
                    {results.map((result, index) => (
                      <TableCell key={index} className="text-center">
                        {result.breakdown.phoneticClarity}/20
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CompareDomainsPage;
